import { toast } from 'sonner';
import { storableError } from 'util/errors';
import { types as sdkTypes } from 'util/sdkLoader';
import { getRegister, searchAttendees as searchAttendeesApi, createAdhocEnrollment } from '../api';

const { UUID } = sdkTypes;

const initialState = {
  register: null,
  fetchInProgress: false,
  fetchError: null,
  removeAttendeeInProgress: false,
  removeAttendeeError: null,
  availableTransitions: [],
  fetchAvailableTransitionsInProgress: false,
  fetchAvailableTransitionsError: null,
  searchAttendeesResults: null,
  searchAttendeesInProgress: false,
  searchAttendeesError: null,
  addAttendeeInProgress: false,
  addAttendeeError: null,
};

const FETCH_REGISTER_REQUEST = 'app/ClassRegisterPage/FETCH_REGISTER_REQUEST';
const FETCH_REGISTER_SUCCESS = 'app/ClassRegisterPage/FETCH_REGISTER_SUCCESS';
const FETCH_REGISTER_ERROR = 'app/ClassRegisterPage/FETCH_REGISTER_ERROR';

const REMOVE_ATTENDEE_REQUEST = 'app/ClassRegisterPage/REMOVE_ATTENDEE_REQUEST';
const REMOVE_ATTENDEE_SUCCESS = 'app/ClassRegisterPage/REMOVE_ATTENDEE_SUCCESS';
const REMOVE_ATTENDEE_ERROR = 'app/ClassRegisterPage/REMOVE_ATTENDEE_ERROR';

const FETCH_AVAILABLE_TRANSITIONS_REQUEST =
  'app/ClassRegisterPage/FETCH_AVAILABLE_TRANSITIONS_REQUEST';
const FETCH_AVAILABLE_TRANSITIONS_SUCCESS =
  'app/ClassRegisterPage/FETCH_AVAILABLE_TRANSITIONS_SUCCESS';
const FETCH_AVAILABLE_TRANSITIONS_ERROR = 'app/ClassRegisterPage/FETCH_AVAILABLE_TRANSITIONS_ERROR';
const CLEAR_AVAILABLE_TRANSITIONS = 'app/ClassRegisterPage/CLEAR_AVAILABLE_TRANSITIONS';

const SEARCH_ATTENDEES_REQUEST = 'app/ClassRegisterPage/SEARCH_ATTENDEES_REQUEST';
const SEARCH_ATTENDEES_SUCCESS = 'app/ClassRegisterPage/SEARCH_ATTENDEES_SUCCESS';
const SEARCH_ATTENDEES_ERROR = 'app/ClassRegisterPage/SEARCH_ATTENDEES_ERROR';
const SEARCH_ATTENDEES_CLEAR = 'app/ClassRegisterPage/SEARCH_ATTENDEES_CLEAR';

const ADD_ATTENDEE_REQUEST = 'app/ClassRegisterPage/ADD_ATTENDEE_REQUEST';
const ADD_ATTENDEE_SUCCESS = 'app/ClassRegisterPage/ADD_ATTENDEE_SUCCESS';
const ADD_ATTENDEE_ERROR = 'app/ClassRegisterPage/ADD_ATTENDEE_ERROR';

const UPDATE_ATTENDEE_ATTENDED = 'app/ClassRegisterPage/UPDATE_ATTENDEE_ATTENDED';

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_REGISTER_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_REGISTER_SUCCESS:
      return { ...state, fetchInProgress: false, register: payload };
    case FETCH_REGISTER_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case REMOVE_ATTENDEE_REQUEST:
      return { ...state, removeAttendeeInProgress: true, removeAttendeeError: null };
    case REMOVE_ATTENDEE_SUCCESS:
      return { ...state, removeAttendeeInProgress: false };
    case REMOVE_ATTENDEE_ERROR:
      return { ...state, removeAttendeeInProgress: false, removeAttendeeError: payload };

    case FETCH_AVAILABLE_TRANSITIONS_REQUEST:
      return {
        ...state,
        fetchAvailableTransitionsInProgress: true,
        fetchAvailableTransitionsError: null,
        availableTransitions: [],
      };
    case FETCH_AVAILABLE_TRANSITIONS_SUCCESS:
      return {
        ...state,
        fetchAvailableTransitionsInProgress: false,
        availableTransitions: payload,
      };
    case FETCH_AVAILABLE_TRANSITIONS_ERROR:
      return {
        ...state,
        fetchAvailableTransitionsInProgress: false,
        fetchAvailableTransitionsError: payload,
      };
    case CLEAR_AVAILABLE_TRANSITIONS:
      return { ...state, availableTransitions: [], fetchAvailableTransitionsError: null };

    case SEARCH_ATTENDEES_REQUEST:
      return { ...state, searchAttendeesInProgress: true, searchAttendeesError: null };
    case SEARCH_ATTENDEES_SUCCESS:
      return { ...state, searchAttendeesInProgress: false, searchAttendeesResults: payload };
    case SEARCH_ATTENDEES_ERROR:
      return { ...state, searchAttendeesInProgress: false, searchAttendeesError: payload };
    case SEARCH_ATTENDEES_CLEAR:
      return { ...state, searchAttendeesResults: null, searchAttendeesError: null };

    case ADD_ATTENDEE_REQUEST:
      return { ...state, addAttendeeInProgress: true, addAttendeeError: null };
    case ADD_ATTENDEE_SUCCESS:
      return { ...state, addAttendeeInProgress: false };
    case ADD_ATTENDEE_ERROR:
      return { ...state, addAttendeeInProgress: false, addAttendeeError: payload };

    case UPDATE_ATTENDEE_ATTENDED: {
      const { attendeeId, attended } = payload;
      if (!state.register) return state;
      return {
        ...state,
        register: {
          ...state.register,
          attendees: state.register.attendees.map((a) =>
            a.id === attendeeId ? { ...a, attended } : a
          ),
        },
      };
    }

    default:
      return state;
  }
}

// The API serves each row as a RegisterEnrollment (CH-428): the attendee and its
// booking-specific data nested under `enrollment`, and `source` naming which store
// the row came from. The dashboard components below still read a flat row, so
// flatten once here at the boundary instead of threading the nesting through every
// consumer.
//
// Two fields are renamed on the way out because the flat row can't hold both:
// the API has `enrollment.attendee.type` (adult/child) and `enrollment.type`
// (the buyer's ticket option) — flattened to `attendeeType` and `ticketOption`.
const flattenRegisterEnrollment = (row, surveyAnswers) => {
  const { attendee, ...enrollment } = row.enrollment ?? {};
  return {
    ...attendee,
    // Keyed by attendee id on the payload; a Sharetribe row's temp string id finds nothing.
    surveyAnswers: surveyAnswers?.[attendee?.id] ?? {},
    attendeeType: attendee?.type ?? null,
    emergencyContacts: enrollment.emergencyContacts ?? [],
    groupId: enrollment.groupId ?? null,
    addons: enrollment.addons ?? [],
    attended: enrollment.attended ?? false,
    enrollmentId: enrollment.id ?? null,
    removal: enrollment.removal ?? null,
    source: row.source,
    customer: row.customer,
    transaction: { ...row.transaction, ticketOption: enrollment.type ?? null },
  };
};

const flattenRegister = (register) =>
  register
    ? {
        ...register,
        attendees: (register.enrolments ?? []).map((row) =>
          flattenRegisterEnrollment(row, register.surveyAnswers)
        ),
      }
    : register;

export const loadData = (params, search) => async (dispatch) => {
  const { slug } = params;
  const date = new URLSearchParams(search).get('date');
  const end = new URLSearchParams(search).get('end');

  if (!date) {
    dispatch({ type: FETCH_REGISTER_SUCCESS, payload: null });
    return;
  }

  dispatch({ type: FETCH_REGISTER_REQUEST });
  try {
    const response = await getRegister({ slug, date, end });
    dispatch({ type: FETCH_REGISTER_SUCCESS, payload: flattenRegister(response.data.register) });
  } catch (e) {
    console.error(e);
    dispatch({ type: FETCH_REGISTER_ERROR, payload: storableError(e) });
  }
};

// Selectors
export const selectRegister = (state) => state.ClassRegisterPage.register;
export const selectFetchInProgress = (state) => state.ClassRegisterPage.fetchInProgress;
export const selectFetchError = (state) => state.ClassRegisterPage.fetchError;
export const selectRemoveAttendeeInProgress = (state) =>
  state.ClassRegisterPage.removeAttendeeInProgress;
export const selectRemoveAttendeeError = (state) => state.ClassRegisterPage.removeAttendeeError;
export const selectAvailableTransitions = (state) => state.ClassRegisterPage.availableTransitions;
export const selectFetchAvailableTransitionsInProgress = (state) =>
  state.ClassRegisterPage.fetchAvailableTransitionsInProgress;

export const removeAttendee =
  ({ transactionId, params, search }) =>
  async (dispatch, getState, sdk) => {
    dispatch({ type: REMOVE_ATTENDEE_REQUEST });
    try {
      await sdk.transactions.transition({
        id: new UUID(transactionId),
        transition: 'transition/provider-cancel',
        params: {},
      });
      dispatch({ type: REMOVE_ATTENDEE_SUCCESS });
      dispatch(loadData(params, search));
    } catch (e) {
      console.error('Failed to remove attendee:', e);
      dispatch({ type: REMOVE_ATTENDEE_ERROR, payload: storableError(e) });
    }
  };

export const fetchAvailableTransitions = (transactionId) => async (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_AVAILABLE_TRANSITIONS_REQUEST });
  try {
    const response = await sdk.processTransitions.query({ transactionId: new UUID(transactionId) });
    const transitions = response.data.data.map((t) => t.attributes.name);
    dispatch({ type: FETCH_AVAILABLE_TRANSITIONS_SUCCESS, payload: transitions });
    return transitions;
  } catch (e) {
    dispatch({ type: FETCH_AVAILABLE_TRANSITIONS_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const clearAvailableTransitions = () => ({ type: CLEAR_AVAILABLE_TRANSITIONS });

// Search attendees
export const selectSearchAttendeesResults = (state) =>
  state.ClassRegisterPage.searchAttendeesResults;
export const selectSearchAttendeesInProgress = (state) =>
  state.ClassRegisterPage.searchAttendeesInProgress;
export const selectSearchAttendeesError = (state) => state.ClassRegisterPage.searchAttendeesError;

export const searchAttendees =
  ({ name }) =>
  async (dispatch) => {
    dispatch({ type: SEARCH_ATTENDEES_REQUEST });
    try {
      const result = await searchAttendeesApi({ name });
      dispatch({
        type: SEARCH_ATTENDEES_SUCCESS,
        payload: {
          attendees: (result?.results ?? []).map((hit) => hit.attendee),
          pagination: result?.pagination?.attendee ?? {
            page: 1,
            perPage: 0,
            total: 0,
            hasNext: false,
          },
        },
      });
    } catch (e) {
      console.error('Failed to search attendees:', e);
      dispatch({ type: SEARCH_ATTENDEES_ERROR, payload: storableError(e) });
    }
  };

export const clearSearchAttendees = () => ({ type: SEARCH_ATTENDEES_CLEAR });

export const updateAttendeeAttended = (attendeeId, attended) => ({
  type: UPDATE_ATTENDEE_ATTENDED,
  payload: { attendeeId, attended },
});

// Add attendee
export const selectAddAttendeeInProgress = (state) => state.ClassRegisterPage.addAttendeeInProgress;
export const selectAddAttendeeError = (state) => state.ClassRegisterPage.addAttendeeError;

export const addAttendee =
  ({ attendee, listing, ticket, consentCollected, params, search, onSuccess }) =>
  async (dispatch) => {
    dispatch({ type: ADD_ATTENDEE_REQUEST });
    try {
      await createAdhocEnrollment({ attendee, listing, ticket, consentCollected });
      dispatch({ type: ADD_ATTENDEE_SUCCESS });
      dispatch(loadData(params, search));
      toast.success(`${attendee.firstName} has been added to the register`);
      onSuccess?.();
    } catch (e) {
      console.error('Failed to add attendee:', e);
      dispatch({ type: ADD_ATTENDEE_ERROR, payload: storableError(e) });
    }
  };
